import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calculator, FilePlus2, Pencil } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { DetailHeader, SummaryRow, SummaryRule } from '@/components/ui/detail';
import { costPerYieldUnit, mayEditVersion, usedInItem } from '../recipeCosting';
import { apiMessage, formatDateTime, formatQuantity } from '../shared';
import type { Explosion, RecipeDetail, RecipeVersion } from './recipeTypes';

interface Props {
  recipeId: string;
  mayWrite: boolean;
  onBack: () => void;
  /** Opens `RecipeVersionEditor` on a DRAFT, or seeded from a published one. */
  onEdit: (recipe: RecipeDetail, version: RecipeVersion | null, seed: RecipeVersion | null) => void;
  /** The list behind this panel refetches when a version is activated. */
  onChanged: () => void;
}

/**
 * ## 64:810 — one recipe, its bill of materials, and what it costs
 *
 * The frame is a routed page; the recipes screen is a list with a dialog, so
 * this is the same detail rendered in place of the list with the trail as the
 * way back.
 *
 * ### The frame's three impossibilities, and what is drawn instead
 *
 * - **`[Edit]` beside `[v1 Active]`.** `PATCH /recipes/versions/:versionId` is
 *   "edit a DRAFT — published versions are immutable" and 400s anything else.
 *   The screen never offers an edit the server would refuse: on a published
 *   version the button is **Copy to new draft**, and the immutability is stated
 *   where Edit would otherwise be.
 * - **"Waste factor 5%" → "Effective cost".** Wastage is
 *   `RecipeComponent.wastagePercent`, per component, "folded into the costed
 *   quantity" — so the explosion's `totalCost` already contains it. Multiplying
 *   a recipe-level factor onto that would double-count, and there is no
 *   recipe-level or version-level waste column to read one from. Neither row is
 *   drawn; the per-component percentage is shown on the line it belongs to.
 * - **"Used in" listing two priced products.** `Recipe.catalogItemId` is
 *   `@unique`: a recipe is the bill of materials of at most one sellable item.
 *   The card shows that one, with its list price — and **not** a margin, because
 *   `GET /inventory/reports/margin` is *realised* margin over a date window, a
 *   different number from list price minus recipe cost, and it is gated on
 *   `reports.view` while the rest of this page needs `inventory.view`.
 *
 * "Cost per gram" is `totalCost / quantity`, computed here: `explodeVersion`
 * returns the total and the per-line costs but no unit cost. The card says the
 * division is the client's.
 */
export function RecipeDetailPanel({ recipeId, mayWrite, onBack, onEdit, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const { format, code, info } = useCurrency();

  const detail = useApi<RecipeDetail>(`/recipes/${recipeId}`);
  const recipe = detail.data;

  const [quantity, setQuantity] = useState('');
  const [explosion, setExplosion] = useState<Explosion | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const active = recipe?.versions?.find((version) => version.status === 'ACTIVE') ?? null;
  const soldAs = recipe ? usedInItem(recipe) : null;

  /**
   * A per-gram ingredient cost carries four decimals, more than any currency's
   * minor unit — rounding it to the currency's scale prints 0.00 for a real
   * cost. Same treatment the order board's COGS panel already uses.
   */
  const preciseMoney = useCallback(
    (value: string) =>
      new Intl.NumberFormat(i18n.language || 'en', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: info.decimals,
        maximumFractionDigits: Math.max(info.decimals, 4),
      }).format(Number(value)),
    [code, i18n.language, info.decimals],
  );

  const explode = useCallback(
    async (qty: string) => {
      setBusy('explode');
      try {
        const { data } = await api.get<Explosion>(`/recipes/${recipeId}/explode`, {
          params: { quantity: qty.trim() || '1' },
        });
        setExplosion(data);
      } catch (e) {
        toast.error(apiMessage(e, t('platform.recipes.explodeFailed')));
      } finally {
        setBusy(null);
      }
    },
    [recipeId, t],
  );

  /**
   * The page opens costed at one batch, so "Total material cost" is the batch
   * cost and "cost per yield unit" is that divided by the yield — which is the
   * pair the frame shows. `explodeRecipe()` refuses a recipe with no active
   * version, so nothing is asked for until there is one.
   */
  useEffect(() => {
    if (!active) return;
    const batch = active.yieldQuantity || '1';
    setQuantity(batch);
    void explode(batch);
  }, [active, explode]);

  if (detail.loading && !recipe) return <LoadingState label={t('common.loading')} />;
  if (detail.error) return <ErrorState message={detail.error} onRetry={detail.reload} />;
  if (!recipe) return <EmptyState title={t('platform.recipes.empty')} />;

  const run = async (key: string, request: () => Promise<unknown>, successKey: string) => {
    setBusy(key);
    try {
      await request();
      toast.success(t(successKey));
      detail.reload();
      onChanged();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const perUnit = explosion ? costPerYieldUnit(explosion) : null;
  const yieldUnit = active?.yieldUnit?.code ?? '';

  return (
    <div className="space-y-4">
      <Breadcrumb
        label={t('platform.recipes.title')}
        trail={[{ label: t('platform.recipes.title'), onClick: onBack }]}
        current={recipe.name}
      />

      <DetailHeader
        title={recipe.name}
        chips={
          active ? (
            <Badge tone={toneForStatus(active.status)}>
              v{active.version} · {t(`platform.recipes.versionStatus.${active.status}`)}
            </Badge>
          ) : (
            <Badge tone="neutral">{t('platform.recipes.noActiveVersion')}</Badge>
          )
        }
        subtitle={recipe.description ?? undefined}
        actions={
          <>
            {mayWrite && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(recipe, null, null)}
              >
                <Icon as={FilePlus2} />
                {t('platform.recipes.editor.newDraft')}
              </Button>
            )}
            <Button
              size="sm"
              disabled={!active || busy === 'explode'}
              title={active ? undefined : t('platform.recipes.costNeedsActiveVersion')}
              onClick={() => void explode(quantity)}
            >
              <Icon as={Calculator} />
              {t('platform.recipes.cost')}
            </Button>
          </>
        }
      />

      <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
        <Card>
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle>
              {t('platform.recipes.billOfMaterials')}
            </CardTitle>
            <div className="flex items-end gap-2">
              {/* Kept: the explosion is per **sold quantity**, and costing one
                  fixed batch answers a different question from costing the 40
                  portions a shift will sell. */}
              <Field label={t('platform.recipes.soldQuantity')}>
                <Input
                  className="w-28"
                  inputMode="decimal"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Field>
              <Button
                size="sm"
                variant="outline"
                disabled={!active || busy === 'explode'}
                onClick={() => void explode(quantity)}
              >
                {t('platform.recipes.recalculate')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {!active ? (
              <EmptyState
                title={t('platform.recipes.noActiveVersion')}
                hint={t('platform.recipes.costNeedsActiveVersion')}
              />
            ) : busy === 'explode' ? (
              <LoadingState label={t('common.loading')} />
            ) : !explosion ? (
              <EmptyState title={t('platform.recipes.noExplosion')} />
            ) : (
              <>
                <TableWrap className="border-0 bg-transparent">
                  <Table>
                    <THead>
                      <TR>
                        <TH>{t('platform.recipes.editor.ingredient')}</TH>
                        <TH className="text-end">{t('common.quantity')}</TH>
                        <TH className="text-end">{t('platform.procurement.unitCost')}</TH>
                        <TH className="text-end">{t('platform.recipes.lineCost')}</TH>
                      </TR>
                    </THead>
                    <tbody>
                      {!explosion.components.length && (
                        <TableEmpty colSpan={4}>
                          {t('platform.recipes.noComponents')}
                        </TableEmpty>
                      )}
                      {explosion.components.map((line) => (
                        <TR key={`${line.inventoryItemId}-${line.via.join('-')}`}>
                          <TD>
                            {line.name}
                            {/* A flattened line can arrive through a nested
                                sub-recipe. The frame's five flat columns cannot
                                say so; the depth annotation can. */}
                            {line.via.length > 1 && (
                              <div className="text-xs text-ink-muted">
                                {t('platform.recipes.viaDepth', { count: line.via.length - 1 })}
                              </div>
                            )}
                          </TD>
                          <TD className="text-end tabular-nums">
                            {formatQuantity(line.quantity)}
                          </TD>
                          <TD className="text-end tabular-nums text-ink-muted">
                            {preciseMoney(line.unitCost)}
                          </TD>
                          <TD className="text-end font-medium tabular-nums">
                            {format(line.cost)}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>

                <SummaryRule />
                <SummaryRow
                  label={t('platform.recipes.totalCost', {
                    qty: formatQuantity(explosion.quantity),
                  })}
                  tone="accent"
                  className="text-base"
                >
                  {format(explosion.totalCost)}
                </SummaryRow>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('platform.recipes.editor.costTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <SummaryRow label={t('platform.recipes.editor.yieldQuantity')}>
                {active
                  ? t('platform.recipes.yields', {
                      qty: formatQuantity(active.yieldQuantity),
                      unit: yieldUnit,
                    })
                  : '—'}
              </SummaryRow>
              <SummaryRow label={t('platform.recipes.editor.perUnit', { unit: yieldUnit || '—' })}>
                {perUnit ? preciseMoney(perUnit) : '—'}
              </SummaryRow>
              {/* No "Waste factor" row and no "Effective cost": wastage is per
                  component and is already inside every figure above. */}
              <p className="pt-1 text-xs text-ink-muted">
                {t('platform.recipes.unitCostClientSide')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('platform.recipes.sellsAs')}</CardTitle>
            </CardHeader>
            <CardContent>
              {soldAs ? (
                <SummaryRow label={soldAs.name}>{format(soldAs.basePrice)}</SummaryRow>
              ) : (
                <p className="text-sm text-ink-muted">
                  {t('platform.recipes.editor.noCatalogItem')}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('platform.recipes.versions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!recipe.versions.length ? (
                <EmptyState
                  title={t('platform.recipes.editor.noVersions')}
                  hint={t('platform.recipes.editor.noVersionsHint')}
                />
              ) : (
                recipe.versions.map((version) => (
                  <div key={version.id} className="space-y-2 rounded-lg border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        v{version.version}
                        <Badge tone={toneForStatus(version.status)}>
                          {t(`platform.recipes.versionStatus.${version.status}`)}
                        </Badge>
                      </span>
                      <span className="text-xs text-ink-muted">
                        {formatDateTime(version.createdAt, i18n.language)}
                      </span>
                    </div>

                    <p className="text-xs text-ink-muted">
                      {t('platform.recipes.yields', {
                        qty: formatQuantity(version.yieldQuantity),
                        unit: version.yieldUnit?.code ?? '',
                      })}
                      {version.components?.length
                        ? ` · ${t('platform.recipes.editor.components')}: ${version.components.length}`
                        : ''}
                    </p>

                    {mayWrite && (
                      <div className="flex flex-wrap gap-1.5">
                        {mayEditVersion(version.status) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onEdit(recipe, version, null)}
                          >
                            <Icon as={Pencil} />
                            {t('platform.recipes.editor.editDraft')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            title={t('platform.recipes.editor.immutableHint')}
                            onClick={() => onEdit(recipe, null, version)}
                          >
                            <Icon as={FilePlus2} />
                            {t('platform.recipes.editor.copyToDraft')}
                          </Button>
                        )}
                        {/* `activateVersion()` refuses an ARCHIVED version
                            outright ("copy it instead") and refuses any version
                            with no components, since it would consume nothing.
                            Activating the already-ACTIVE version is a no-op, so
                            it is not offered. */}
                        {version.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            disabled={
                              busy === `activate:${version.id}` || !version.components?.length
                            }
                            title={
                              version.components?.length
                                ? undefined
                                : t('platform.recipes.activateNeedsComponents')
                            }
                            onClick={() =>
                              void run(
                                `activate:${version.id}`,
                                () => api.post(`/recipes/versions/${version.id}/activate`, {}),
                                'platform.recipes.activated',
                              )
                            }
                          >
                            {t('platform.recipes.activate')}
                          </Button>
                        )}
                        {version.status !== 'ARCHIVED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy === `archive:${version.id}`}
                            onClick={() =>
                              void run(
                                `archive:${version.id}`,
                                () => api.post(`/recipes/versions/${version.id}/archive`, {}),
                                'platform.recipes.archived',
                              )
                            }
                          >
                            {t('platform.recipes.archive')}
                          </Button>
                        )}
                      </div>
                    )}

                    {!mayEditVersion(version.status) && (
                      <p className="text-xs text-ink-muted">
                        {t('platform.recipes.editor.immutableHint')}
                      </p>
                    )}

                    {!!version.components?.length && (
                      <ul className="space-y-1 text-xs">
                        {version.components.map((component) => (
                          <li
                            key={component.id}
                            className="flex flex-wrap items-baseline justify-between gap-2"
                          >
                            <span>
                              {component.inventoryItem?.name ??
                                component.subRecipe?.name ??
                                t('platform.recipes.unknownComponent')}
                              {component.subRecipe && (
                                <Badge tone="info" className="ms-2">
                                  {t('platform.recipes.subRecipe')}
                                </Badge>
                              )}
                              {component.isOptional && (
                                <Badge tone="neutral" className="ms-2">
                                  {t('platform.recipes.optional')}
                                </Badge>
                              )}
                            </span>
                            <span className="tabular-nums text-ink-muted">
                              {formatQuantity(component.quantity)} {component.unit?.code ?? ''}
                              {Number(component.wastagePercent) > 0 &&
                                ` · ${t('platform.recipes.editor.wastage')} ${formatQuantity(component.wastagePercent)}%`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
